import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { log } from '@/services/Log'
import type { BCCartsType, BCCartType } from '@/types/BigCommerce/BCCart'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'

const formCartStandardizer = ({
  generateLineItemsFromBCCart,
  getCustomItems,
  standardizeCart,
}: {
  generateLineItemsFromBCCart: Awaited<typeof import('@/services/Cart/generateLineItemsFromBCCart')>['generateLineItemsFromBCCart'],
  getCustomItems: Awaited<typeof import('@/helpers/checkoutHelpers/index')>['getCustomItems'],
  standardizeCart: Awaited<typeof import('@/services/Standardizers/standardizeCart')>['standardizeCart'],
}) => async (bcCart: BCCartType) => {
  const lineItems = await generateLineItemsFromBCCart(bcCart)
  const customItems = getCustomItems({ lineItems, customItems: bcCart?.lineItems?.customItems })
  const standardizedCart = standardizeCart({
    ...bcCart,
    email: bcCart.email || '',
    lineItems: [...lineItems, ...customItems],
  })
  return standardizedCart
}

export const asyncStandardizeCart = async (bcCart: BCCartType): Promise<StandardCart> => {
  const [
    { standardizeCart },
    { generateLineItemsFromBCCart },
    { getCustomItems },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Standardizers/standardizeCart'),
    import('@/services/Cart/generateLineItemsFromBCCart'),
    import('@/helpers/checkoutHelpers/index'),
  ])
  const standardizer = formCartStandardizer({
    generateLineItemsFromBCCart,
    getCustomItems,
    standardizeCart,
  })
  return standardizer(bcCart)
}

export const getCart = async () => {
  log('getCart START')
  // Returns a Cart. - https://developer.bigcommerce.com/api-reference/storefront/carts/cart/getacart
  const [
    { default: Axios },
    { standardizeCart },
    { generateLineItemsFromBCCart },
    { getCustomItems },
    { assertBCCart, assertBCCarts },
  ] = await allPromisesWithRetries(() => [
    import('axios'),
    import('@/services/Standardizers/standardizeCart'),
    import('@/services/Cart/generateLineItemsFromBCCart'),
    import('@/helpers/checkoutHelpers/index'),
    import('@/types/BigCommerce/BCCart'),
  ])
  const { data } = await Axios.get<BCCartsType>('/api/storefront/cart?include=lineItems.physicalItems.options')
  assertBCCarts(data)
  const rawCart: BCCartType = data[0]
  if (!rawCart) {
    return null
  }
  assertBCCart(rawCart)
  // Adds isFurniture boolean property and availability message to lineItems

  const standardizer = formCartStandardizer({
    generateLineItemsFromBCCart,
    getCustomItems,
    standardizeCart,
  })

  const standardCart = await standardizer(rawCart)
  log('getCart END', { cart: standardCart })
  return standardCart
}

export default getCart
